// Internal modal body for <CancelRide> — the cancellation flow's screens
// (Plan change / policy / Cancel Anyway / the two "Ride Cancelled" screens).
// Do NOT import this directly; render trips-cancellation via <CancelRide>.
import {useState, useCallback, useEffect} from 'react'
import {useNavigate} from 'react-router'
import {Dialog, DialogBackdrop, DialogPanel} from '@headlessui/react'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import {personIcon, supportingPerson, supportingPersonGrey, freeTierIcon, planCarLogo} from "@img/index.js";
import Loader from '@/customComponents/uiComponents/Loader.jsx'
import {RadioButton} from '@/customComponents/uiComponents/RadioButton.jsx'
import {isNearAirport} from '@/utils/airports'
import {formatPrice, parseCard, isDriverRevealed} from '@/utils/functions'
import {BOOKING_CONSTRAINTS} from '@/constants/booking'
import {parseAppZoneDate, formatTripLocalTime, formatDHM} from '@/utils/dateUtils'

// Non-airport trips. No "Other" row — the free-text box below the list is always
// open, so anything off-list is typed there instead of behind a checkbox.
const CANCEL_REASONS = ['Taking too long to find a driver', 'Driver is late to pick-up', "My driver and I couldn't connect", 'Changed my mind / no longer need the ride', 'Booked by mistake', 'Found another ride', 'Fare/price too high', 'My pick-up location was incorrect']
// fromAirport pickups — some reasons are flight-specific (early landing, wrong flight
// details); several overlap with the generic list.
const CANCEL_REASONS_FROM_AIRPORT = ['Driver is late to pick-up', "My driver and I couldn't connect", 'Landed early / already made other arrangements', 'Entered wrong flight details', 'Changed my mind / no longer need the ride', 'Booked by mistake', 'Fare/price too high']

// The clock glyph shared by the "Late Cancel" tier badge and the "Cancel Anyway?" fee
// chip — one path so the two can never drift. Drawn rather than loaded because the old
// greenClock.svg was just a ring with a dot in it (no clock face), and inline SVG lets
// each caller pick the stroke colour. Size/colour come from the caller; the 2px round
// stroke matches free-tier-icon.svg's tick so the badges read as one set.
const ClockGlyph = ({className, color}) => (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke={color}
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9"/>
        <path d="M12 7.5V12l3 2"/>
    </svg>
)

// A tier cut-off ("2026-07-15 04:20:00") → "Until 4:20 AM today|tomorrow|Jul 15",
// in the VIEWER's timezone.
//
// `ends_at` is APP_TIMEZONE wall-clock. It used to be parsed with `new Date(str)` — read
// as the visitor's local time, then printed back through local getters, so the Toronto
// clock came out verbatim. That put this badge in a different frame from the "Pickup in
// 46m" countdown beside it (server-computed on real instants): in Toronto they agreed, but
// nine zones away the cut-off read as hours in the past. parseAppZoneDate resolves the
// real instant so both now describe the same moment to whoever is looking.
//
// `now` is the browser's clock, NOT tripNow(): once the cut-off prints in the viewer's
// zone, "today" has to mean the viewer's today, or a late-evening Toronto deadline gets
// the wrong day word.
function tierUntil(endsAt) {
    if (!endsAt) return ''
    const d = parseAppZoneDate(endsAt)
    if (!d || isNaN(d.getTime())) return ''
    const now = new Date()
    const tomorrow = new Date(now)
    tomorrow.setDate(now.getDate() + 1)
    const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
    const dayWord = sameDay(d, now) ? 'today' : sameDay(d, tomorrow) ? 'tomorrow' : d.toLocaleDateString('en-US', {month: 'short', day: 'numeric'})
    return `Until ${formatTripLocalTime(d)} ${dayWord}`
}

// Timing, fee, refund tiers, driver, and charged card all come from the trip
// (`trip.cancellation_policy`, `trip.driver`, `trip.selected_payment_method`).
// The free screen's minimum-booking-gap now comes from /config/app
// (BOOKING_CONSTRAINTS.MINIMUM_TIME_BUFFER_MINUTES). Still hard-coded copy: the
// driver "4 mins away" ETA (no field for it yet).
//
// `onCancel(reason)` must return a Promise resolving truthy on a successful
// cancel — the popup then advances to its own success screen instead of the
// caller closing/navigating. Callers that return undefined keep the legacy
// behaviour (they own the terminal navigation).
export default function CancelRideModal({isOpen, onClose, onCancel, isCancelling, trip, loadingTrip = false}) {
    const navigate = useNavigate()
    const [view, setView] = useState('main')   // main | feeConfirm | doneFree | doneFee
    const [cancelReason, setCancelReason] = useState('')
    const [cancelOther, setCancelOther] = useState('')
    // "Are you sure?" confirm popup. Holds the done-screen to advance to once the user
    // taps Yes: 'doneFree' (free tier "Cancel Ride") or 'doneFee' (paid "Cancel and Pay").
    // null = popup closed.
    const [confirmTarget, setConfirmTarget] = useState(null)

    // A reason is still REQUIRED, but there is no "Other" row any more: it can be a
    // ticked row, free text, or both (joined) — whichever the user gave.
    const typedReason = cancelOther.trim()
    const effectiveReason = [cancelReason, typedReason].filter(Boolean).join(' — ')

    // Cancellation policy — data-driven timing / fee / refund tiers.
    const cp = trip?.cancellation_policy
    const hoursUntil = cp?.hours_until_pickup
    // A Draft (unconfirmed return leg / rebook) has `cancellation_policy: null` by design —
    // it isn't a live booking yet, so discarding it is unrestricted and free. Same signal as
    // an empty tier list, so route it down the no-bands path rather than letting a null
    // policy fall through to the PAID branch (which is what `!!cp?.free_cancellation` does).
    // Keyed on the STATUS, not on `cp == null`: a list row also arrives without a policy
    // (CancelRide fetches the detail lazily), and that must not read as free.
    const isUnrestricted = trip?.status === 'Draft'
    // `tiers: undefined` = NOT loaded (the caller fetches the real policy — render no
    // schedule meanwhile); `tiers: []` = present + empty = "no bands" = unconditionally
    // free (the single "Free cancellation" fallback card). These are NOT the same.
    const tiersLoaded = Array.isArray(cp?.tiers)
    const tiers = tiersLoaded ? cp.tiers : []
    const noBands = isUnrestricted || (tiersLoaded && tiers.length === 0)
    const activeTier = tiers.find((t) => t.is_active)
    // The tier the modal reflects = the API's ACTIVE tier. Every free/paid decision below
    // reads from THIS. The tier cards are DISPLAY-ONLY (they show the schedule) — NOT
    // user-selectable, so the applied fee/refund can't be changed by tapping a band.
    const selectedTier = activeTier
    // A present-empty tier list (noBands) = unconditionally free, so the whole flow
    // (Cancel Ride → doneFree, no fee) behaves as free — NOT via cp.free_cancellation,
    // which is absent exactly when there are no bands. (undefined tiers ⇒ not treated as
    // free — we simply don't have the data yet.)
    const freeAvailable = noBands
        ? true
        : (selectedTier ? !!selectedTier.is_free : !!cp?.free_cancellation?.available)
    const feeCents = selectedTier?.fee_amount ?? cp?.fee_amount ?? cp?.active_tier?.fee_amount ?? 0
    const activeLabel = selectedTier?.label ?? cp?.active_tier?.label

    // Reasons list depends on trip direction — a fromAirport pickup (origin near an
    // airport) gets flight-specific reasons. Unknown coords → generic list.
    const isFromAirport = !!isNearAirport(trip?.origin?.lat, trip?.origin?.lng)
    const reasons = isFromAirport ? CANCEL_REASONS_FROM_AIRPORT : CANCEL_REASONS
    const {brand: cardBrand, last4: cardLast4} = parseCard(trip?.selected_payment_method)
    const chargedCard = [cardBrand ? cardBrand.charAt(0).toUpperCase() + cardBrand.slice(1) : '', cardLast4 ? `•••• ${cardLast4}` : ''].filter(Boolean).join(' ')

    // Minimum lead time before a new booking's pickup, from /config/app. Read INSIDE the
    // component (it is a getter over the react-query cache, empty until the config lands).
    const minTimeBufferMins = BOOKING_CONSTRAINTS.MINIMUM_TIME_BUFFER_MINUTES

    // Driver card visibility — the same privacy gate as the trip page: identity is only
    // disclosed once the driver is ON THE WAY (EnRoute) or the trip has started. Unlike
    // the trip page there is NO dummy/blurred stand-in here; the card is simply absent
    // until then.
    //
    // Latched rather than read live: cancelling flips the status to Cancelled, so a live
    // read would make the card vanish between the "Cancel Anyway?" and "Ride Cancelled"
    // screens. Once revealed it stays revealed for this opening, and resets on close.
    const [driverRevealed, setDriverRevealed] = useState(false)
    useEffect(() => {
        if (!isOpen) { setDriverRevealed(false); return }
        if (isDriverRevealed(trip)) setDriverRevealed(true)
    }, [isOpen, trip])

    // Real driver details (present once a driver has accepted). Show a "-" when
    // the trip response has no data yet — the placeholder avatar covers a missing
    // photo the same way.
    const drv = trip?.driver
    const driverName = drv?.name || '-'
    const vehicleStr = drv?.vehicle
        ? [[drv.vehicle.color, drv.vehicle.make, drv.vehicle.model].filter(Boolean).join(' '), drv.vehicle.plate_no].filter(Boolean).join(' • ')
        : ''
    const driverVehicle = vehicleStr || '-'
    const driverAvatar = drv?.photo
        ? <img src={drv.photo} alt={driverName} className="w-15 h-15 rounded-full object-cover shrink-0 bg-(--bg-grey)"/>
        : <span className="w-15 h-15 rounded-full bg-(--bg-grey) flex items-center justify-center shrink-0"><img src={personIcon} alt="" className="w-5 h-auto opacity-40"/></span>

    // Cancellation-fee receipt link — captured from the CANCEL response (onCancel may
    // return it as a string). The `trip` shown here isn't refetched until the modal
    // closes, so the fresh fee_receipt_link only exists in that response, not on `trip`.
    const [feeReceiptLink, setFeeReceiptLink] = useState(null)

    const handleClose = useCallback(() => {
        onClose?.()
        setConfirmTarget(null)
        setTimeout(() => {
            setCancelReason('')
            setCancelOther('')
            setFeeReceiptLink(null)
            setView('main')
        }, 250)
    }, [onClose])

    const doCancel = useCallback(async (nextView) => {
        // onCancel returns the fee-receipt link (string) on a paid cancel, else truthy.
        const res = await onCancel?.(effectiveReason)
        if (res) {
            if (typeof res === 'string') setFeeReceiptLink(res)
            setView(nextView)
        }
    }, [onCancel, effectiveReason])

    // "Yes" on the confirm popup — run the cancel, advancing to the tier's done screen
    // on success, then close the popup (whether it succeeded or failed).
    const confirmYes = useCallback(async () => {
        if (!confirmTarget) return
        await doCancel(confirmTarget)
        setConfirmTarget(null)
    }, [confirmTarget, doCancel])

    const goBookNew = useCallback(() => { handleClose(); navigate('/book-ride') }, [handleClose, navigate])
    // "View Receipt" here is shown on the PAID-tier "Ride Cancelled" screen, so it must
    // open the CANCELLATION FEE receipt (`fee_receipt_link`) — NOT the trip receipt.
    // Prefer the link captured from the cancel response; fall back to the trip object,
    // then the ride-details page. Opens in a new tab (direct button click = user gesture).
    const goReceipt = useCallback(() => {
        const link = feeReceiptLink || trip?.fee_receipt_link
        if (link) { window.open(link, '_blank', 'noopener,noreferrer'); return }
        handleClose(); navigate(`/rides/${trip?.id ?? ''}`)
    }, [feeReceiptLink, handleClose, navigate, trip?.fee_receipt_link, trip?.id])

    // Every step of the flow (Plan change / Cancel Anyway / both Ride Cancelled screens)
    // shares one 48px heading — kept in one place so they can't drift apart.
    const stepHeading = 'font-bold text-5xl max-md:text-[28px] mb-1! text-[#201E20]'

    // Shown on BOTH the Plan-change screen (under the reason box, as a heads-up) and
    // the free "Ride Cancelled" screen — one node so the copy can't drift.
    const minGapNote = (
        <p className="flex items-start gap-2 text-(--text-secondary) text-sm max-md:text-xs mb-0!">
            <svg viewBox="0 0 24 24" className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="#8f8d8d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 16v-4M12 8h.01"/></svg>
            <span>You can't create another booking minimum time of <strong className="font-bold text-(--text-primary)">{formatDHM(minTimeBufferMins)}</strong></span>
        </p>
    )

    // The row surface shared by the Plan-change ACTIVE tier, the "All set" row on the
    // free Ride-Cancelled screen and the fee row on "Cancel Anyway?" — pill-shaped with
    // a soft grey-blue fill. One constant so those three can't drift apart; an
    // unselected tier keeps the plain white/bordered variant.
    const rowBase = 'flex items-center gap-4 rounded-[50px] p-4 border'
    const activeCard = `${rowBase} border-[#E2E8F0] bg-[#F1F5F9]`
    const idleCard = `${rowBase} border-(--border-color) bg-white`

    // The "free" / "paid" verdict pill in the Plan-change sentence: pill-shaped
    // (50px radius), 18px side padding, weight 800. Free carries the pale brand mint;
    // paid keeps the red.
    const markFree = 'bg-[#A9FFE3] text-[#201E20] rounded-[50px] px-[18px] max-md:px-3 py-[2px] font-extrabold'
    const markPaid = 'bg-[#ff6969] text-white rounded-[50px] px-[18px] max-md:px-3 py-[2px] font-extrabold'

    // Amount pill — the saturated mint/red pair, so a charge reads the same on the tier
    // cards and the fee/refund screens. Deliberately stronger than the pale <mark> fill
    // above: this pill is a small target and needs the contrast to stay legible.
    const amountFree = 'font-bold text-2xl max-md:text-lg shrink-0 rounded-lg px-3 max-md:px-2 bg-[#00E5A0] text-[#201E20]'
    const amountPaid = 'font-bold text-2xl max-md:text-lg shrink-0 rounded-lg px-3 max-md:px-2 bg-[#ff6969] text-white'
    // A tier whose window isn't the active one is greyed outright — a real grey fill
    // rather than the mint/red pill at reduced opacity, so it never reads as a washed-out
    // version of the live verdict.
    const amountMuted = 'font-bold text-2xl max-md:text-lg shrink-0 rounded-lg px-3 max-md:px-2 bg-[#e6e6e6] text-[#6B7280]'

    // Reusable footer button styles. btnDark is the lone full-width button (doneFree);
    // every other footer is a two-up row, which stays side by side on mobile — so the
    // `btnPair*` variants shed padding and type there to fit two on one line.
    const btnDark = 'cursor-pointer w-full py-5 max-md:py-4 rounded-full bg-(--text-primary) text-white text-base max-md:text-sm flex items-center justify-center min-h-13.75 max-md:min-h-11 border-0'

    const btnPairBase = 'cursor-pointer flex-1 py-4 max-md:py-3 px-2 rounded-full text-base max-md:text-xs flex items-center justify-center text-center leading-tight min-h-13.75 max-md:min-h-11 border-0 disabled:opacity-50 disabled:cursor-not-allowed'
    const btnPairDark = `${btnPairBase} bg-(--text-primary) text-white`
    const btnPairFee = `${btnPairBase} bg-(--bg-grey) text-[#ff7a2f] font-semibold`
    const btnPairOutline = `${btnPairBase} bg-white border border-(--border-color) text-(--text-primary) font-semibold`

    // ── Footer per view ──
    // While the full trip is still being fetched (opened from a policy-less list row)
    // there's nothing to act on yet — no footer, just the loader body below.
    let footer = null
    if (loadingTrip) {
        footer = null
    } else if (view === 'main') {
        footer = (
            /* Side by side on every width — the pair splits the row rather than
               stacking full-width on mobile. */
            <div className="flex gap-3 items-center max-md:gap-2">
                <button type="button" onClick={handleClose}
                        className={`${btnPairDark} min-w-0`}>
                    Keep the Booking
                </button>
                <button type="button" onClick={freeAvailable ? () => setConfirmTarget('doneFree') : () => setView('feeConfirm')} disabled={isCancelling || !effectiveReason}
                        className={`${btnPairFee} min-w-0`}>{isCancelling ? <Loader color="#ff7a2f" height={18}/> : 'Cancel Ride'}</button>
            </div>
        )
    } else if (view === 'feeConfirm') {
        footer = (
            <div className="flex gap-3 max-md:gap-2">
                <button type="button" onClick={handleClose} className={`${btnPairDark} min-w-0`}>Go Back to Ride</button>
                <button type="button" onClick={() => setConfirmTarget('doneFee')} disabled={isCancelling} className={`${btnPairFee} min-w-0`}>{isCancelling ? <Loader color="#ff7a2f" height={18}/> : `Cancel and Pay ${formatPrice(feeCents)}`}</button>
            </div>
        )
    } else if (view === 'doneFree') {
        footer = <button type="button" onClick={goBookNew} className={btnDark}>Book New Ride</button>
    } else if (view === 'doneFee') {
        footer = (
            <div className="flex gap-3 max-md:gap-2">
                <button type="button" onClick={goBookNew} className={`${btnPairDark} min-w-0`}>Book New Ride</button>
                <button type="button" onClick={goReceipt} className={`${btnPairOutline} min-w-0`}>View Receipt</button>
            </div>
        )
    }

    // ── Header back / close per view ──
    // feeConfirm's back returns to the Plan-change screen; the main screen's back just
    // closes the modal (same as the cross). Done screens show no back.
    const headerBack = view === 'feeConfirm' ? () => setView('main') : view === 'main' ? handleClose : undefined
    const headerClose = handleClose

    return (
        <>
        <Modal isOpen={isOpen} onClose={handleClose} footer={footer}>
            {/* Elegant buttons (48px white circle + shadow) — dropping `outlineButtons`
                and the two icon overrides restores the simple variant's defaults, which
                ARE the elegant look. */}
            <ModalHeader
                title=""
                onClose={headerClose}
                onBack={headerBack}
            />

            {loadingTrip ? (
                /* ── Loading the full trip (opened from a policy-less list row) ── */
                <div className="hopn-view-in flex items-center justify-center py-20">
                    <Loader/>
                </div>
            ) : view === 'feeConfirm' ? (
                /* ── Cancel Anyway? (late-cancel confirmation, no cancel yet) ── */
                <div className="hopn-view-in">
                    <h3 className={stepHeading}>Cancel Anyway?</h3>
                    <p className="text-(--text-secondary) mb-6 max-md:mb-4 max-md:text-sm">Cancelling now will result in a fee.</p>

                    <div className={`${activeCard} mb-3`}>
                        {/* Same clock as the "Late Cancel" tier badge, in the fee palette:
                            neutral chip, red hands — this screen IS the charge. */}
                        <span className="w-11 h-11 rounded-[50px] bg-[#EFEFEF] flex items-center justify-center shrink-0">
                            <ClockGlyph className="w-[22px] h-[22px]" color="#DF0000"/>
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className="font-bold text-xl max-md:text-base mb-0! leading-tight">Late Cancellation Fee</p>
                            <p className="text-(--text-secondary) max-md:text-xs mb-0! leading-tight">{activeLabel || 'Within 1 hr of pickup'}</p>
                        </div>
                        <span className={amountPaid}>{formatPrice(feeCents)}</span>
                    </div>

                    {driverRevealed && (
                        <div className="flex items-center gap-3 border border-(--border-color) rounded-3xl p-4">
                            {driverAvatar}
                            <div className="flex-1 min-w-0">
                                <p className="font-bold text-xl max-md:text-base mb-0! leading-tight">{driverName} is - mins away</p>
                                <p className="text-(--text-secondary) max-md:text-xs mb-0! leading-tight">{driverVehicle}</p>
                            </div>
                        </div>
                    )}
                </div>
            ) : view === 'doneFree' ? (
                /* ── Ride Cancelled — free (no charge) ── */
                <div className="hopn-view-in">
                    <h3 className={stepHeading}>Ride Cancelled</h3>
                    <p className="text-(--text-secondary) mb-6 max-md:mb-4 max-md:text-sm">Your request has been successfully cancelled.</p>

                    <div className={`${activeCard} mb-6`}>
                        {/* Same 32px mint tick the active "Free Now" tier uses — this row is
                            the free outcome, so it should read as the same badge. */}
                        <img src={freeTierIcon} alt="" className="w-8 h-8 shrink-0"/>
                        <div className="flex-1 min-w-0">
                            <p className="font-bold text-xl max-md:text-base mb-0! leading-tight">All set</p>
                            <p className="text-(--text-secondary) max-md:text-xs mb-0! leading-tight">No charge on your credit card</p>
                        </div>
                        <span className={amountFree}>$0</span>
                    </div>

                    {minGapNote}
                </div>
            ) : view === 'doneFee' ? (
                /* ── Ride Cancelled — fee charged ── */
                <div className="hopn-view-in">
                    <h3 className={stepHeading}>Ride Cancelled</h3>
                    <p className="text-(--text-secondary) mb-6 max-md:mb-4 max-md:text-sm">A {formatPrice(feeCents)} cancellation fee has been charged</p>

                    <div className="bg-white shadow-[0px_4px_10px_rgba(0,0,0,0.05)] rounded-3xl p-6 mb-4">
                        {driverRevealed && (
                            <div className="flex items-center gap-3 mb-4">
                                {driverAvatar}
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-xl max-md:text-base mb-0! leading-tight">{driverName}</p>
                                    <p className="text-(--text-secondary) max-md:text-xs mb-0! leading-tight">{driverVehicle}</p>
                                </div>
                            </div>
                        )}
                        {/* The divider only exists to separate the fee from the driver row —
                            without that row it would read as a stray line under the header. */}
                        <div className={`flex items-center justify-between gap-3 ${driverRevealed ? 'border-t border-(--border-color) pt-4' : ''}`}>
                            <div className="min-w-0">
                                <p className="font-bold text-lg max-md:text-base mb-0! leading-tight">Late Cancellation Fee</p>
                                <p className="text-(--text-secondary) max-md:text-xs mb-0! leading-tight">{chargedCard ? `Charged to ${chargedCard}` : 'Charged to your card'}</p>
                            </div>
                            <span className={amountPaid}>{formatPrice(feeCents)}</span>
                        </div>
                    </div>

                    <div className="bg-[#B1FBE3] border border-[#CFCDCD] rounded-3xl p-5">
                        <p className="flex items-center gap-2 font-bold mb-2! max-md:text-sm">
                            <img src={supportingPerson} alt="" className="w-4 h-auto"/> Supporting your driver
                        </p>
                        <p className="text-(--text-secondary) text-sm max-md:text-xs mb-0!">The fee goes directly to your driver who has already committed to your trip and is on the way.</p>
                    </div>
                </div>
            ) : (
                /* ── Main: "Plan change?" with selectable tiers + reasons ── */
                <div className="hopn-view-in">
                    <h3 className={stepHeading}>Plan change?</h3>
                    <p className="text-[#0A0A0AA3] font-extrabold mb-6 max-md:mb-4 max-md:text-sm">We keep it flexible for you. Here's how it works:</p>

                    <div className="flex items-center gap-2 mb-5 text-(--text-primary)">
                        {/* Same live-pulse treatment as the header/sidebar notification dot
                            (see NotifyDot.jsx): a solid --accent-green dot with a ping ripple
                            expanding out of it. NotifyDot itself can't be reused here — it is
                            absolutely positioned to sit on a badge corner, whereas this one is
                            an inline flex item, so the outer span goes `relative` instead. */}
                        <span className="relative w-2 h-2 rounded-full bg-(--accent-green) shrink-0">
                            <span className="absolute inline-flex w-full h-full bg-(--accent-green) rounded-full opacity-75 animate-ping"/>
                        </span>
                        {/* The free/paid verdict is the line's payload, so the word itself is
                            highlighted — mint for free, red for paid. */}
                        <span className="text-[28px] max-md:text-lg leading-snug">
                            Pickup in <strong className="font-bold">{formatDHM(hoursUntil, 'hours')}</strong> —{' '}
                            {freeAvailable ? (
                                <>canceling now is <mark className={markFree}>free</mark></>
                            ) : (
                                <>canceling is now <mark className={markPaid}>paid</mark></>
                            )}
                        </span>
                    </div>

                    {/* Refund tiers — DISPLAY-ONLY schedule (two-up on desktop, stacked on
                        mobile). The ACTIVE tier gets a dark border + shadow + green tick; the
                        others a light border + clock. Not clickable — the applied fee follows
                        the API's active tier, never a tapped one. An EMPTY tiers array =
                        unconditionally free → a single "Free cancellation" card. */}
                    <div className="flex flex-wrap gap-3 mb-4">
                        {tiers.length ? tiers.map((t) => {
                            const isSel = t.band === selectedTier?.band
                            // Description: while the tier's window is still open (is_active) show
                            // its cut-off time ("Until 7:10 AM tomorrow"). Once it has lapsed the
                            // free tier reads "Free cancellation time will passed away!" and a paid
                            // tier "You have to pay in this".
                            const tierDesc = t.is_active
                                ? tierUntil(t.ends_at)
                                : t.is_free
                                    ? 'Free cancellation time will passed away!'
                                    : 'You have to pay in this'
                            return (
                            /* The column wraps the card so a PAID tier can carry its
                               explainer directly underneath it, in its own half of the
                               grid — the note belongs to that card, not to the section. */
                            <div key={t.band} className="basis-[calc(50%-6px)] max-md:basis-full">
                                <div className={isSel ? activeCard : idleCard}>
                                    {/* Both badges are the SAME 32px circle — #00E5A0 fill with a
                                        #0A0A0A 2px glyph — only the glyph differs (tick vs clock).
                                        The active tier's comes from free-tier-icon.svg; the clock
                                        is drawn inline because greenClock.svg was only a ring with
                                        a dot in it (no clock face). Keep the fill/stroke in step
                                        with that asset if either ever changes. */}
                                    {isSel ? (
                                        <img src={freeTierIcon} alt="" className="w-8 h-8 shrink-0"/>
                                    ) : (
                                        <span className="w-8 h-8 rounded-full bg-[#00E5A0] flex items-center justify-center shrink-0">
                                            <ClockGlyph className="w-[18px] h-[18px]" color="#0A0A0A"/>
                                        </span>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <p className={`font-bold text-lg max-md:text-base mb-0! leading-tight ${isSel ? '' : 'text-[#6B7280]'}`}>{t.is_free ? 'Free Now' : 'Late Cancel'}</p>
                                        <p className={`mb-0! leading-tight text-sm max-md:text-xs ${isSel ? 'text-(--text-secondary)' : 'text-[#6B7280]'}`}>{tierDesc}</p>
                                    </div>
                                    {/* Active tier gets the mint/red pill matching the "free" /
                                        "paid" <mark> above; the others get the flat grey one. */}
                                    <span className={isSel ? (t.is_free ? amountFree : amountPaid) : amountMuted}>
                                        {formatPrice(t.fee_amount)}
                                    </span>
                                </div>
                                {/* Always under a paid tier, whether or not it's the active one. */}
                                {!t.is_free && (
                                    <p className="flex items-center gap-2 text-(--text-secondary) text-sm max-md:text-[11px] mt-2 mb-0! px-4 max-md:px-3">
                                        <img src={supportingPersonGrey} alt="" className="w-3 h-auto shrink-0"/>
                                        This fee helps cover your driver's reserved time
                                    </p>
                                )}
                            </div>
                            )
                        }) : noBands ? (
                            <div className={`basis-full ${activeCard}`}>
                                <img src={freeTierIcon} alt="" className="w-8 h-8 shrink-0"/>
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-lg max-md:text-base mb-0! leading-tight">Free cancellation</p>
                                    <p className="text-(--text-secondary) mb-0! leading-tight text-sm max-md:text-xs">Cancel any time</p>
                                </div>
                                <span className={amountFree}>{formatPrice(0)}</span>
                            </div>
                        ) : null}
                    </div>

                    <h4 className="font-bold text-2xl max-md:text-xl mb-3!">Reason</h4>
                    <div className="border border-(--border-color) rounded-(--radius-2xl) p-5 max-md:p-4 mb-4">
                        {reasons.map((r, i) => (
                            <label key={i} className={`flex items-center gap-3 cursor-pointer text-sm max-md:text-xs py-4 max-md:py-3 ${i < reasons.length - 1 ? 'border-b border-[#EFEFEF]' : ''}`}>
                                <RadioButton type="checkbox" checked={cancelReason === r} onChange={() => setCancelReason(cancelReason === r ? '' : r)}/>
                                {r}
                            </label>
                        ))}
                    </div>
                    {/* Always open (it replaced the removed "Other" row). */}
                    <textarea value={cancelOther} onChange={e => setCancelOther(e.target.value)} placeholder="Enter your cancel reason" rows={3}
                              className="w-full! bg-[#EFEFEF] border border-(--border-color) rounded-(--radius-2xl) p-5 max-md:p-4 text-base max-md:text-sm outline-none resize-none"/>

                    {/* Same minimum-gap heads-up as the "Ride Cancelled" screen, shown here
                        BEFORE the user commits rather than only after. */}
                    <div className="mt-4">{minGapNote}</div>
                </div>
            )}
        </Modal>

        <ConfirmCancelPopup
            isOpen={!!confirmTarget}
            onYes={confirmYes}
            onNo={() => setConfirmTarget(null)}
            isBusy={isCancelling}
        />
        </>
    )
}

// "Are you sure want to cancel the ride?" — a small confirm popup shown ON TOP of the
// cancel modal (z above its z-99999) before the cancellation is actually committed:
// the free-tier "Cancel Ride" button and the paid-tier "Cancel and Pay $X" button both
// open this first, and only "Yes" runs the cancel. Yes/No are side-by-side on desktop,
// stacked on mobile.
function ConfirmCancelPopup({isOpen, onYes, onNo, isBusy}) {
    return (
        <Dialog open={isOpen} onClose={onNo} transition className="relative z-[100000]">
            <DialogBackdrop transition className="fixed inset-0 bg-[#0000005c] backdrop-blur-[10px] transition duration-200 ease-out data-closed:opacity-0"/>
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <DialogPanel transition className="w-full max-w-120 bg-white rounded-4xl px-7 py-9 text-center shadow-[0px_20px_50px_rgba(0,0,0,0.18)] transition duration-200 ease-out data-closed:scale-95 data-closed:opacity-0">
                    {/* Brand mark — plan-car-logo.svg already carries its own rounded mint
                        badge, so it's placed as-is (no extra background container). */}
                    <img src={planCarLogo} alt="Hop'n" className="h-12 max-md:h-9 w-auto inline-block mb-5 max-md:mb-4"/>
                    <h4 className="font-bold text-lg max-md:text-base text-(--text-primary) mb-7! max-md:mb-5!">Are you sure want to cancel the ride</h4>
                    {/* Side by side on mobile too — they used to stack full-width. */}
                    <div className="flex gap-3 max-md:gap-2">
                        <button type="button" onClick={onYes} disabled={isBusy}
                                className="cursor-pointer flex-1 min-w-0 py-4 max-md:py-3 rounded-full bg-(--bg-grey) text-(--text-primary) text-base max-md:text-sm font-semibold border-0 min-h-13.75 max-md:min-h-11 flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed">
                            {isBusy ? <Loader height={18}/> : 'Yes'}
                        </button>
                        <button type="button" onClick={onNo} disabled={isBusy}
                                className="cursor-pointer flex-1 min-w-0 py-4 max-md:py-3 rounded-full bg-(--text-primary) text-white text-base max-md:text-sm font-semibold border-0 min-h-13.75 max-md:min-h-11 disabled:opacity-60">
                            No
                        </button>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    )
}
